import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Dropdown, Modal, Row, Spinner, Table } from 'react-bootstrap';
import classNames from 'classnames';
import { format, parseISO } from 'date-fns';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from '../../../common/Flex';
import CardDropdown from '../../../common/CardDropdown';
import PaginationEllipsis from '../../../common/PaginationEllipsis';
import SearchAccordion from './SearchAccordion';
import { _exportPDF } from '../../../../lib/api/api';
import {
  itemMatch,
  listMatches,
  patchMatchActive,
  removeMatch
} from '../../../../lib/api/match';
import AddMatch from './AddMatch';
import EditMatch from './EditMatch';
import MatchScoreboard from '../MatchScoreboard';

const formatMatchTime = value => (value ? String(value).slice(0, 5) : '');

export const MatchSchedule = () => {
  const [search, setSearch] = useState({});
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusLoadingGuid, setStatusLoadingGuid] = useState(null);
  const [editLoadingGuid, setEditLoadingGuid] = useState(null);
  const [viewLoadingGuid, setViewLoadingGuid] = useState(null);
  const [deleteLoadingGuid, setDeleteLoadingGuid] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);

  useEffect(() => {
    fetchData();
  }, [page, pageSize, search]);

  const fetchData = async (showLoader = true) => {
    if (showLoader) {
      setLoading(true);
    }
    try {
      const response = await listMatches({ page, limit: pageSize, ...search });
      const nextItems = response?.items || [];
      const total = Number(response?.total || 0);
      const limit = Number(response?.limit || pageSize || 100);

      setItems(nextItems);
      setTotalPages(Math.max(1, Math.ceil(total / limit)));
    } catch (error) {
      console.error('Greska prilikom ucitavanja utakmica:', error);
      setItems([]);
      setTotalPages(0);
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  };

  const handlePageChange = newPage => {
    if (newPage > 0 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleSearch = data => {
    setPage(1);
    setSearch(data);
  };

  const handleEdit = async matchesGuid => {
    setEditLoadingGuid(matchesGuid);
    try {
      const response = await itemMatch({ matches_guid: matchesGuid });
      const matchItem = Array.isArray(response) ? response[0] : response;
      setSelectedMatch(matchItem);
      setShowEditModal(true);
    } catch (error) {
      console.error('Greska prilikom ucitavanja utakmice:', error);
    } finally {
      setEditLoadingGuid(null);
    }
  };

  const handleView = async matchesGuid => {
    setViewLoadingGuid(matchesGuid);
    try {
      const response = await itemMatch({ matches_guid: matchesGuid });
      const matchItem = Array.isArray(response) ? response[0] : response;
      setSelectedMatch(matchItem);
      setShowViewModal(true);
    } catch (error) {
      console.error('Greska prilikom ucitavanja utakmice:', error);
    } finally {
      setViewLoadingGuid(null);
    }
  };

  const handleDelete = async matchesGuid => {
    const isConfirmed = window.confirm(
      'Da li ste sigurni da želite obrisati utakmicu?'
    );

    if (!isConfirmed) {
      return;
    }

    setDeleteLoadingGuid(matchesGuid);
    try {
      await removeMatch(matchesGuid);
      toast.success('Utakmica je uspješno obrisana.');
      await fetchData(false);
    } catch (error) {
      console.error('Greska prilikom brisanja utakmice:', error);
      toast.error(
        error?.body?.message ||
          error?.message ||
          'Došlo je do greške prilikom brisanja.'
      );
    } finally {
      setDeleteLoadingGuid(null);
    }
  };

  const handleToggleActive = async item => {
    setStatusLoadingGuid(item.matches_guid);
    try {
      await patchMatchActive(item.matches_guid, {
        is_active: Number(item.is_active) === 1 ? 0 : 1
      });
      toast.success('Status utakmice je uspješno izmijenjen.');
      await fetchData(false);
    } catch (error) {
      console.error('Greska prilikom izmjene statusa utakmice:', error);
      toast.error(
        error?.body?.message ||
          error?.message ||
          'Došlo je do greške prilikom izmjene statusa.'
      );
    } finally {
      setStatusLoadingGuid(null);
    }
  };

  const handlePrintReport = async matchesGuid => {
    try {
      await _exportPDF(`report/pdf-match-report/${matchesGuid}`);
    } catch (error) {
      console.error('Greska prilikom stampe izvjestaja:', error);
      toast.error(
        error?.body?.message ||
          error?.message ||
          'Doslo je do greske prilikom pripreme stampe izvjestaja.'
      );
    }
  };

  const renderActions = item => (
    <CardDropdown>
      <div className="py-2">
        <Dropdown.Item
          disabled={viewLoadingGuid === item.matches_guid}
          onClick={() => handleView(item.matches_guid)}
        >
          Pregled podataka
        </Dropdown.Item>
        <Dropdown.Divider />
                                <Dropdown.Item
                                  disabled={statusLoadingGuid === item.matches_guid}
                                  onClick={() => handleToggleActive(item)}
                                >
                                  {Number(item.is_active) === 1
                                    ? 'Skoni aktivan'
                                    : 'Postavi kao aktivan'}
                                </Dropdown.Item>
                                {item.match_reports_id ? (
                                  <>
                                    <Dropdown.Divider />
                                    <Dropdown.Item
                                      onClick={() =>
                                        handlePrintReport(item.matches_guid)
                                      }
                                    >
                                      Štampa izvještaja
                                    </Dropdown.Item>
                                  </>
                                ) : null}
                                <Dropdown.Divider />
                                <Dropdown.Item
                                  disabled={editLoadingGuid === item.matches_guid}
                                  onClick={() => handleEdit(item.matches_guid)}
                                >
          Edit
        </Dropdown.Item>
        <Dropdown.Divider />
        <Dropdown.Item
          className="text-danger"
          disabled={deleteLoadingGuid === item.matches_guid}
          onClick={() => handleDelete(item.matches_guid)}
        >
          Delete
        </Dropdown.Item>
      </div>
    </CardDropdown>
  );

  return (
    <>
      <Row className="g-3">
        <SearchAccordion handleSearch={handleSearch} />
        <Col xs={12}>
          <Card className={classNames('h-100')}>
            <Card.Header>
              <Row className={classNames('g-2')}>
                <Col>
                  <Flex>
                    <h5 className="mb-0 hover-actions-trigger text-truncate text-nowrap">
                      Raspored Utakmica
                    </h5>
                  </Flex>
                </Col>
                <Col xs="auto">
                  <Button onClick={() => setShowAddModal(true)} variant="primary">
                    Dodaj utakmicu
                  </Button>
                </Col>
              </Row>
            </Card.Header>

            <Card.Body className="bg-body-tertiary">
              {loading ? (
                <div className="text-center">
                  <Spinner animation="border" role="status" />
                </div>
              ) : (
                <Row className="mb-3">
                  <div className="d-none d-xl-block">
                    <Table responsive>
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Takmičenja</th>
                          <th className="text-center">Datum</th>
                          <th>Parovi / Rezultat</th>
                          <th className="text-center">Aktiv.</th>
                          <th className="text-end">Akcije</th>
                        </tr>
                      </thead>
                      <tbody className="fs-10">
                        {items.map(item => (
                          <tr key={item.matches_guid || item.match_id}>
                            <td>
                              <div className="ms-2">{item.match_id}</div>
                            </td>
                            <td>
                              <div className="ms-2">
                                <div>{item.competitions_name}</div>
                                <div className="text-body-tertiary">
                                  {item.round_type_name}
                                </div>
                              </div>
                            </td>
                            <td className="text-center align-middle text-nowrap">
                              <div>
                                <div>
                                  {item.date
                                    ? format(parseISO(item.date), 'dd. MMM yy.')
                                    : ''}
                                </div>
                                <div className="text-body-tertiary">
                                  {formatMatchTime(item.match_time)}
                                </div>
                              </div>
                            </td>
                            <td>
                              <MatchScoreboard item={item} />
                            </td>
                            <td className="text-center align-middle">
                              {Number(item.is_active) === 1 ? (
                                <FontAwesomeIcon icon="eye" />
                              ) : null}
                            </td>
                            <td className="text-end">{renderActions(item)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  <div className="d-xl-none">
                    <Row className="g-3">
                      {items.map(item => (
                        <Col xs={12} key={item.matches_guid || item.match_id}>
                          <div
                            className="border rounded-4 bg-white h-100 p-3 shadow-sm"
                            style={{ borderColor: '#dfe3ee' }}
                          >
                            <div className="d-flex align-items-start justify-content-between gap-3">
                              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                <div className="fw-semibold text-dark fs-9">
                                  {item.competitions_name || '-'}
                                </div>
                              </div>
                              <div className="flex-shrink-0">{renderActions(item)}</div>
                            </div>

                            <div className="d-flex flex-wrap gap-2 mt-2">
                              <span className="badge rounded-pill badge-subtle-primary">
                                #{item.match_id || '-'}
                              </span>
                              <span className="badge rounded-pill badge-subtle-success">
                                {item.round_type_name || '-'}
                              </span>
                              {Number(item.is_active) === 1 && (
                                <span className="badge rounded-pill badge-subtle-info">
                                  Aktivan
                                </span>
                              )}
                            </div>

                            <div className="text-600 fs-10 mt-2">
                              {item.date ? format(parseISO(item.date), 'dd. MMM yy.') : '-'}
                              {item.match_time ? ` u ${formatMatchTime(item.match_time)}` : ''}
                            </div>

                            <div className="mt-2 fs-10">
                              <MatchScoreboard item={item} />
                            </div>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>
                </Row>
              )}
            </Card.Body>

            <Card.Footer>
              <Row className="justify-content-center">
                <PaginationEllipsis
                  page={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </Row>
            </Card.Footer>
          </Card>
        </Col>
      </Row>

      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Dodaj utakmicu</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <AddMatch
            initialCompetitionId={search?.competitions_id}
            onClose={() => setShowAddModal(false)}
            onSave={fetchData}
          />
        </Modal.Body>
      </Modal>

      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Izmijeni utakmicu</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedMatch && (
            <EditMatch
              initialData={selectedMatch}
              onClose={() => setShowEditModal(false)}
              onSave={() => fetchData(false)}
            />
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Pregled podataka utakmice</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedMatch && (
            <EditMatch
              initialData={selectedMatch}
              onClose={() => setShowViewModal(false)}
              readOnly
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default MatchSchedule;
